import { skipToken } from "@reduxjs/toolkit/query";
import type { AdminApiAiUsageByItem } from "@repo/app-types/entities/creditUsage";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { UsageBasedFeatureKey } from "@repo/app-types/helpers/aiFeatureCatalog";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { useMemo, type FC } from "react";
import { useAiUsageQuery } from "../../api/entities/company.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectCompany } from "../company/companySlice.js";
import { formatCreditsUsed } from "../settingsPage/billingSettings/usageFormatting.js";

/** One feature's current-period AI credits per item, for a table's Credits column. */
export interface AiCreditsByItem {
  /** Undefined while the usage loads or when it can't be read. */
  itemsById: ReadonlyMap<string, AdminApiAiUsageByItem> | undefined;
}

/**
 * The current billing period's AI credits for each of a feature's items. Null
 * for a company without billing, a teammate who can't read it, or a failed
 * read, whose tables leave the column out.
 */
export function useAiCreditsByItem(
  featureKey: UsageBasedFeatureKey,
): AiCreditsByItem | null {
  const company = useAppSelector(selectCompany);
  const canReadBilling = useTeammateAuthorization().can(
    PermissionActions.READ,
    PermissionSubjects.BILLING,
  );
  const isVisible = Boolean(company?.billing_company_id) && canReadBilling;
  const { currentData: usage, isError } = useAiUsageQuery(
    isVisible ? { periodOffset: 0 } : skipToken,
  );
  return useMemo(() => {
    if (!isVisible || isError) {
      return null;
    }
    const feature = usage?.features.find(
      (candidate) => candidate.feature_key === featureKey,
    );
    return {
      itemsById: feature
        ? new Map(
            feature.items.flatMap<[string, AdminApiAiUsageByItem]>((item) =>
              item.item_id === null ? [] : [[item.item_id, item]],
            ),
          )
        : undefined,
    };
  }, [isVisible, isError, usage, featureKey]);
}

/** "Credits", with a tooltip naming the period the figures cover. */
export const AiCreditsColumnHeader: FC = () => (
  <TooltipTrigger>
    <TooltipHoverTarget shape="rounded">
      <span className="whitespace-nowrap">Credits</span>
    </TooltipHoverTarget>
    <SimpleTooltip>
      AI credits used in the current billing period.
    </SimpleTooltip>
  </TooltipTrigger>
);

/** An item's credits: "—" until usage loads, "Included" for unbilled usage. */
export function formatItemAiCredits(
  credits: AiCreditsByItem,
  itemId: string,
): string {
  if (credits.itemsById === undefined) {
    return "—";
  }
  const item = credits.itemsById.get(itemId);
  if (item?.is_free) {
    return "Included";
  }
  return formatCreditsUsed(item?.credits_used ?? 0);
}

/** The Credits column of an entity table whose rows are the feature's items. */
export function buildAiCreditsColumn<T extends { id: string }>(
  credits: AiCreditsByItem,
): EntityTableColumnDefinition<T> {
  return {
    id: "aiCredits",
    header: <AiCreditsColumnHeader />,
    className: "w-28 text-right",
    renderCell: (entity) => (
      <span className="block text-right text-muted-foreground tabular-nums">
        {formatItemAiCredits(credits, entity.id)}
      </span>
    ),
  };
}
